import React, { useMemo } from 'react';
import Pivot from '../Pivot';
import { appendPivotSubtotals } from './pivot-subtotals';

const ROW_DIM_KEYS_BY_GROUP = {
  project: ['projectId', 'projectName'],
  'project.practice': ['projectPracticeId', 'projectPracticeName'],
  'project.businessUnit': ['projectBusinessUnitId', 'projectBusinessUnitName'],
  client: ['clientId', 'clientName'],
  'client.practice': ['clientPracticeId', 'clientPracticeName'],
  'client.businessUnit': ['clientBusinessUnitId', 'clientBusinessUnitName'],
};

const TOOLTIP_CONFIG = {
  columns: ['Budget', 'Actuals', 'Left', 'Used'],
  groups: [
    { label: 'Hours', cells: ['hoursBudget', 'hours', 'hoursBudgetLeft', 'hoursBudgetUsedRatio'] },
    {
      label: 'Billable Hours',
      cells: ['billableHoursBudget', 'billableHours', 'billableHoursBudgetLeft', 'billableHoursBudgetUsedRatio'],
    },
    {
      label: 'Non-Billable Hours',
      cells: [
        'nonBillableHoursBudget',
        'nonBillableHours',
        'nonBillableHoursBudgetLeft',
        'nonBillableHoursBudgetUsedRatio',
      ],
    },
    {
      label: 'Internal Hours',
      cells: ['internalHoursBudget', 'internalHours', 'internalHoursBudgetLeft', 'internalHoursBudgetUsedRatio'],
    },
  ],
};

function aggregate(group) {
  const sum = (key) => group.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);
  const ratio = (numKey, denKey) => {
    const den = sum(denKey);
    return den ? sum(numKey) / den : null;
  };

  return {
    hours: sum('hours'),
    billableHours: sum('billableHours'),
    nonBillableHours: sum('nonBillableHours'),
    internalHours: sum('internalHours'),

    billableHoursBudget: sum('billableHoursBudget'),
    nonBillableHoursBudget: sum('nonBillableHoursBudget'),
    internalHoursBudget: sum('internalHoursBudget'),
    hoursBudget: sum('hoursBudget'),

    billableHoursBudgetLeft: sum('billableHoursBudget') - sum('billableHours'),
    nonBillableHoursBudgetLeft: sum('nonBillableHoursBudget') - sum('nonBillableHours'),
    internalHoursBudgetLeft: sum('internalHoursBudget') - sum('internalHours'),
    hoursBudgetLeft: sum('hoursBudget') - sum('hours'),

    billableHoursBudgetUsedRatio: ratio('billableHours', 'billableHoursBudget'),
    nonBillableHoursBudgetUsedRatio: ratio('nonBillableHours', 'nonBillableHoursBudget'),
    internalHoursBudgetUsedRatio: ratio('internalHours', 'internalHoursBudget'),
    hoursBudgetUsedRatio: ratio('hours', 'hoursBudget'),
  };
}

export default function TimeMonthlyBudgetPivot({ data, fields, meta, pagination, filename, cellValue }) {
  const enrichedData = useMemo(() => {
    const group = meta?.applied?.input?.group ?? [];
    const hasPeriod = group.some((g) => g.field === 'period');
    const rowDimGroups = group.filter((g) => g.field !== 'period');
    if (!hasPeriod || rowDimGroups.length === 0) return data;

    const rowDimKeys = rowDimGroups.flatMap((g) => ROW_DIM_KEYS_BY_GROUP[g.field] ?? []);
    if (rowDimKeys.length === 0) return data;

    return appendPivotSubtotals({
      rows: data,
      rowDimKeys,
      periodKeys: ['periodStart', 'periodEnd'],
      aggregate,
    });
  }, [data, meta]);

  return (
    <Pivot
      data={enrichedData}
      fields={fields}
      meta={meta}
      pagination={pagination}
      filename={filename}
      tooltipConfig={TOOLTIP_CONFIG}
      cellValue={cellValue}
    />
  );
}
